import React, { useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import dayjs from "dayjs";

import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DataUsageOutlinedIcon from "@mui/icons-material/DataUsageOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EventNoteIcon from "@mui/icons-material/EventNote";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

import { DatabaseContext, UserContext } from "../../../../context";
import firebase from "../../../../firebase";
import { deleteFileRecordAndNotifications } from "../fileDeletion";
import {
    doesFileHaveDuplicate,
    shouldFileBeVisible,
} from "../../../../utility";
import NoPermissionsMessage from "../../../NoPermissionsMessage";
import ToolbarWithLogo from "../../../ToolbarWithLogo";
import DeleteFileDialog from "../DeleteFileDialog";
import EditFileDialog from "../EditFileDialog";
import FileEventsDialog from "../FileEventsDialog";
import FileUsageDialog from "../FileUsageDialog";
import ShareLinkDialog from "../ShareLinkDialog";
import { useBlobFileDownload } from "../useFileDownloads";
import StandardFileViewerLayout from "./StandardFileViewerLayout";

export default function StandardFileViewerPage({
    fileKey,
    routeSegment,
    renderMedia,
    renderAdditionalActions,
    mediaBackground,
    mediaPadding,
    showDownloadAction = true,
    showPublicShareAction = true,
    deleteFromStorage = true,
}) {
    const history = useHistory();
    const location = useLocation();
    const downloadFile = useBlobFileDownload();
    const {
        files,
        comments,
        events,
        users,
        folders,
        groups,
    } = useContext(DatabaseContext);
    const { currentUserData } = useContext(UserContext);

    const [file, setFile] = useState();
    const [folder, setFolder] = useState();
    const [uploadedBy, setUploadedBy] = useState("");
    const [userHasPermission, setUserHasPermission] = useState();
    const [userIsManager, setUserIsManager] = useState(false);
    const [isInFavourites, setIsInFavourites] = useState(false);
    const [hasLoggedUsage, setHasLoggedUsage] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [confirmDeleteDialogIsOpen, setConfirmDeleteDialogIsOpen] =
        useState(false);
    const [eventDialogOpen, setEventDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [usageDialogOpen, setUsageDialogOpen] = useState(false);
    const [shareLinkDialogOpen, setShareLinkDialogOpen] = useState(false);

    useEffect(() => {
        const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
            if (!user) {
                history.replace("/signin?url=" + location.pathname);
            }
        });

        const selectedFile = files.find((item) => item.key === fileKey);

        if (selectedFile && currentUserData) {
            setFile(selectedFile);
            setFolder(
                folders.find((item) => item.key === selectedFile.folder),
            );
            setUserHasPermission(
                shouldFileBeVisible(
                    selectedFile,
                    groups,
                    currentUserData,
                ),
            );
            setIsInFavourites(
                (currentUserData.faveFiles || []).includes(selectedFile.key),
            );
            setUserIsManager(
                (selectedFile.managers || []).includes(currentUserData.key) ||
                    currentUserData.role === "System Digital Manager" ||
                    selectedFile.uploadedBy === currentUserData.key,
            );

            const uploader = users.find(
                (user) => user.key === selectedFile.uploadedBy,
            );
            setUploadedBy(
                uploader ? `${uploader.firstName} ${uploader.lastName}` : "",
            );

            if (!hasLoggedUsage) {
                firebase.database().ref("fileusage").push({
                    file: selectedFile.key,
                    fileName: selectedFile.displayName,
                    fileType: selectedFile.type,
                    user: currentUserData.key,
                    action: "Open",
                    date: dayjs().toString(),
                });
                setHasLoggedUsage(true);
            }
        }

        return () => unsubscribe();
    }, [
        currentUserData,
        fileKey,
        files,
        folders,
        groups,
        hasLoggedUsage,
        history,
        location.pathname,
        users,
    ]);

    function handleBackToFiles() {
        const returnTo = location.state?.returnTo;

        if (returnTo && returnTo.startsWith("/files")) {
            history.goBack();
            return;
        }

        history.push(file?.folder ? `/files/${file.folder}` : "/files");
    }

    function handleFavourite() {
        const favourites = currentUserData.faveFiles || [];
        const updatedFavourites = isInFavourites
            ? favourites.filter((favourite) => favourite !== file.key)
            : [...favourites, file.key];
        const favouritesObject = {};

        updatedFavourites.forEach((favourite) => {
            favouritesObject[favourite] = true;
        });

        firebase
            .database()
            .ref("users")
            .child(currentUserData.key)
            .update({ faveFiles: favouritesObject });
    }

    function handleCopyLinkClick() {
        navigator.clipboard.writeText(
            `${window.location.origin}/${routeSegment}/${file.key}`,
        );
        setAnchorEl(null);
    }

    function handleDownloadClick() {
        setAnchorEl(null);
        downloadFile(file);
    }

    async function handleConfirmDeletePressed() {
        if (!userIsManager) {
            return;
        }

        comments.forEach((comment) => {
            if (comment.file === file.key) {
                firebase
                    .database()
                    .ref("comments")
                    .child(comment.key)
                    .update({ deleted: true });
            }
        });

        events.forEach((event) => {
            const remainingFiles = (event.files || [])
                .filter((eventFile) => eventFile.key !== file.key)
                .map((eventFile) => eventFile.key);

            if (remainingFiles.length !== (event.files || []).length) {
                const filesObject = {};
                remainingFiles.forEach((remainingFile) => {
                    filesObject[remainingFile] = true;
                });
                firebase
                    .database()
                    .ref("events")
                    .child(event.key)
                    .update({ files: filesObject });
            }
        });

        users.forEach((user) => {
            const remainingFiles = (user.files || []).filter(
                (userFile) => userFile !== file.key,
            );

            if (remainingFiles.length !== (user.files || []).length) {
                const filesObject = {};
                remainingFiles.forEach((remainingFile) => {
                    filesObject[remainingFile] = true;
                });
                firebase
                    .database()
                    .ref("users")
                    .child(user.key)
                    .update({ files: filesObject });
            }
        });

        if (
            deleteFromStorage &&
            !doesFileHaveDuplicate(file, files)
        ) {
            const storageFileKey = file.original || file.key;
            firebase
                .storage()
                .ref("files")
                .child(storageFileKey)
                .child(file.filename)
                .delete();
        }

        await deleteFileRecordAndNotifications(file.key);
        setConfirmDeleteDialogIsOpen(false);
        handleBackToFiles();
    }

    return (
        <>
            <StandardFileViewerLayout
                file={file}
                folder={folder}
                uploadedBy={uploadedBy}
                userHasPermission={userHasPermission}
                media={file ? renderMedia(file) : null}
                mediaBackground={mediaBackground}
                mediaPadding={mediaPadding}
                onBack={handleBackToFiles}
                onFavourite={handleFavourite}
                isInFavourites={isInFavourites}
                onMore={(event) => setAnchorEl(event.currentTarget)}
                primaryActions={
                    <>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<EventNoteIcon />}
                            onClick={() => setEventDialogOpen(true)}
                            sx={{
                                flex: {
                                    xs: "1 1 calc(50% - 4px)",
                                    sm: "0 0 auto",
                                },
                            }}
                        >
                            Events
                        </Button>
                        {file &&
                            renderAdditionalActions?.(file, {
                                onDownload: handleDownloadClick,
                            })}
                    </>
                }
            />

            <Menu
                id={`${routeSegment}-viewer-menu`}
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
            >
                <MenuItem onClick={handleCopyLinkClick}>
                    <ListItemIcon>
                        <ContentCopyOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    Copy Link
                </MenuItem>
                {showPublicShareAction &&
                    (userIsManager || file?.allowSharing) && (
                    <MenuItem
                        onClick={() => {
                            setAnchorEl(null);
                            setShareLinkDialogOpen(true);
                        }}
                    >
                        <ListItemIcon>
                            <PublicOutlinedIcon fontSize="small" />
                        </ListItemIcon>
                        Share Public Link
                    </MenuItem>
                    )}
                {userIsManager && (
                    <MenuItem
                        onClick={() => {
                            setAnchorEl(null);
                            setEditDialogOpen(true);
                        }}
                    >
                        <ListItemIcon>
                            <EditOutlinedIcon fontSize="small" />
                        </ListItemIcon>
                        Edit File
                    </MenuItem>
                )}
                {userIsManager && (
                    <MenuItem
                        onClick={() => {
                            setAnchorEl(null);
                            setUsageDialogOpen(true);
                        }}
                    >
                        <ListItemIcon>
                            <DataUsageOutlinedIcon fontSize="small" />
                        </ListItemIcon>
                        File Usage Data
                    </MenuItem>
                )}
                {showDownloadAction && (
                    <MenuItem
                        disabled={!file?.allowDownloads}
                        onClick={handleDownloadClick}
                    >
                        <ListItemIcon>
                            <DownloadOutlinedIcon fontSize="small" />
                        </ListItemIcon>
                        Download File
                    </MenuItem>
                )}
                {userIsManager && <Divider />}
                {userIsManager && (
                    <MenuItem
                        onClick={() => {
                            setAnchorEl(null);
                            setConfirmDeleteDialogIsOpen(true);
                        }}
                        sx={{ color: "error.main" }}
                    >
                        <ListItemIcon sx={{ color: "error.main" }}>
                            <DeleteOutlineIcon fontSize="small" />
                        </ListItemIcon>
                        Delete File
                    </MenuItem>
                )}
            </Menu>

            {confirmDeleteDialogIsOpen && (
                <DeleteFileDialog
                    open
                    onClose={() => setConfirmDeleteDialogIsOpen(false)}
                    onConfirm={handleConfirmDeletePressed}
                    fileName={file?.displayName}
                />
            )}
            {userHasPermission === false && <NoPermissionsMessage />}
            <ToolbarWithLogo />
            {eventDialogOpen && (
                <FileEventsDialog
                    open
                    onClose={() => setEventDialogOpen(false)}
                    file={file}
                />
            )}
            {editDialogOpen && (
                <EditFileDialog
                    open
                    onClose={() => {
                        // EditFileDialog shows its own save confirmation
                        setEditDialogOpen(false);
                    }}
                    file={file}
                    folder={folder}
                />
            )}
            {usageDialogOpen && (
                <FileUsageDialog
                    open
                    onClose={() => setUsageDialogOpen(false)}
                    file={file}
                />
            )}
            {showPublicShareAction && shareLinkDialogOpen && (
                <ShareLinkDialog
                    open
                    onClose={() => setShareLinkDialogOpen(false)}
                    file={file}
                />
            )}
        </>
    );
}
